import React, { useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { ExpressionNumberInput } from '@components/common/ExpressionNumberInput';
import { ReferenceDropdown } from './ReferenceDropdown';
import { LineItemsEditor } from './LineItemsEditor';
import { TaxItemsEditor } from './TaxItemsEditor';
import type { PendingDocument, DocumentFieldsResponse } from '@schemas/api';
import type { DocumentFieldOption } from '@/types/onboarding';

interface EditableExtractedFieldsProps {
  document: PendingDocument;
  isLoading?: boolean;
  perFieldConf?: Record<string, number>;
  confidenceThreshold?: number;
  excludeLineItems?: boolean;
  documentType?: 'purchase_order' | 'receipt' | 'invoice';
  selectedFields?: string[];
  fieldCatalog?: DocumentFieldOption[];
  erpConnectionId?: string;
}

export interface EditableExtractedFieldsHandle {
  getEditedData: () => Record<string, any>;
  hasChanges: () => boolean;
}

type FieldType = 'text' | 'number' | 'date' | 'vendor' | "purchase_order" | 'array' | 'tax_items';

interface ExtractedField {
  key: string;
  type: FieldType;
  label: string;
  value: any;
  originalValue: any;
  candidates?: Array<{ value: any; confidence: number; provenance: string }>;
}

function unwrapArrayFieldValue(value: any): any[] {
  if (Array.isArray(value) && value.length > 0 && Array.isArray(value[0]?.value)) {
    return value[0].value;
  }

  return Array.isArray(value) ? value : [];
}

function unwrapTaxItemsFieldValue(
  value: any
): NonNullable<DocumentFieldsResponse['tax_items']> {
  const unwrappedValue = unwrapArrayFieldValue(value);

  return unwrappedValue.filter(
    (item): item is NonNullable<DocumentFieldsResponse['tax_items']>[number] =>
      Boolean(item && typeof item === 'object')
  );
}

function normalizeTaxItems(
  taxItems?: DocumentFieldsResponse['tax_items']
): DocumentFieldsResponse['tax_items'] {
  if (!Array.isArray(taxItems)) {
    return null;
  }

  const normalizedItems = taxItems
    .filter((item): item is NonNullable<DocumentFieldsResponse['tax_items']>[number] => Boolean(item && typeof item === 'object'))
    .map((item) => {
      const normalizedItem: Record<string, any> = {};

      Object.entries(item).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (trimmedValue) {
            normalizedItem[key] = trimmedValue;
          }
          return;
        }

        if (value !== null && value !== undefined) {
          normalizedItem[key] = value;
        }
      });

      return normalizedItem;
    })
    .filter((item) => Object.keys(item).length > 0);

  return normalizedItems.length > 0 ? normalizedItems : null;
}

export const EditableExtractedFields = forwardRef<EditableExtractedFieldsHandle, EditableExtractedFieldsProps>(
  ({ document, isLoading = false, perFieldConf = {}, confidenceThreshold = 0.9, excludeLineItems = false, documentType = 'invoice', selectedFields, fieldCatalog = [], erpConnectionId }, ref) => {
    const [editedData, setEditedData] = useState<Record<string, any>>(document.extraction_data || {});

    // Expose methods for parent components to access edited data
    useImperativeHandle(
      ref,
      () => ({
        getEditedData: () => editedData,
        hasChanges: () => JSON.stringify(editedData) !== JSON.stringify(document.extraction_data || {}),
      }),
      [editedData, document.extraction_data]
    );

  // Detect field type based on key and document type
  const detectFieldType = useCallback((key: string): FieldType => {
    // Document identifiers - always plain text
    if (key === 'po_number' || key === 'receipt_number' || key === 'invoice_number') {
      return 'text';
    }

    // References to other objects - searchable dropdowns
    if (key === 'vendor_name') return 'vendor';
    if (key === 'po_number_reference' || (key.includes('po_number') && key !== 'po_number')) return "purchase_order";
    if (key === 'line_items' || key === 'lines') return 'array';
    if (key === 'tax_items') return 'tax_items';

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('date')) return 'date';
    if (lowerKey.includes('qty') || lowerKey.includes('quantity') ||
        lowerKey.includes('price') || lowerKey.includes('total') ||
        lowerKey.includes('amount') || lowerKey.includes('tax') ||
        lowerKey.includes('subtotal')) {
      return 'number';
    }

    return 'text';
  }, []);

  // Extract the chosen value from candidates
  const getChosenValue = useCallback((fieldData: any): any => {
    if (fieldData === null || fieldData === undefined) return '';

    // If it's an array (multiple candidates)
    if (Array.isArray(fieldData)) {
      if (fieldData.length === 0) return '';

      // If first element has 'value' property (extracted structure)
      if (fieldData[0] && typeof fieldData[0] === 'object' && 'value' in fieldData[0]) {
        return fieldData[0].value;
      }

      // Otherwise return first element as-is
      return fieldData[0];
    }

    // If it's a direct value
    return fieldData;
  }, []);

  // Parse extracted fields
  const fields = useMemo(() => {
    const parsed: ExtractedField[] = [];
    const fieldLabelByKey = new Map(
      fieldCatalog
        .filter((option) => !option.key.startsWith('line_items.'))
        .map((option) => [option.key, option.label])
    );
    // When Admin Settings has configured fields, it is the source of truth for
    // the review layout. Start from that ordered list instead of every key the
    // extractor happened to return; configured keys are retained even when
    // their extracted value is null or absent so a reviewer can correct them.
    const orderedFieldKeys = selectedFields
      ? selectedFields.filter((key) => !key.startsWith('line_items.'))
      : Object.keys(editedData);

    orderedFieldKeys.forEach((key) => {
      const value = editedData[key];
      if (key === 'line_items' || key === 'lines') {
        parsed.push({
          key,
          type: 'array',
          label: 'Line Items',
          value: unwrapArrayFieldValue(value),
          originalValue: value,
        });
      } else if (key === 'tax_items') {
        parsed.push({
          key,
          type: 'tax_items',
          label: 'Tax Lines',
          value: unwrapTaxItemsFieldValue(value),
          originalValue: value,
        });
      } else {
        const type = detectFieldType(key);
        const chosenValue = getChosenValue(value);

        parsed.push({
          key,
          type,
          label: fieldLabelByKey.get(key) ?? key.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          value: chosenValue,
          originalValue: value,
          candidates: Array.isArray(value) && value.length > 0 && typeof value[0] === 'object'
            ? (value as Array<{ value: any; confidence: number; provenance: string }>)
            : [],
        });
      }
    });

    return parsed;
  }, [editedData, detectFieldType, fieldCatalog, getChosenValue, selectedFields]);

  const handleFieldChange = useCallback((key: string, newValue: any) => {
    setEditedData((prev) => {
      const updated = { ...prev };

      // If original was an array of candidates, update the first candidate's value
      if (Array.isArray(prev[key]) && prev[key].length > 0 && typeof prev[key][0] === 'object' && 'value' in prev[key][0]) {
        updated[key] = [{ ...prev[key][0], value: newValue }, ...prev[key].slice(1)];
      } else {
        updated[key] = newValue;
      }

      return updated;
    });
  }, []);

  const handleLineItemsChange = useCallback((newItems: any[]) => {
    setEditedData((prev) => {
      const updated = { ...prev };

      // Determine which key is being used (lines or line_items)
      const lineItemsKey = 'lines' in prev ? 'lines' : 'line_items';

      // Update line_items/lines value, preserving confidence and provenance
      if (Array.isArray(prev[lineItemsKey]) && prev[lineItemsKey].length > 0 && typeof prev[lineItemsKey][0] === 'object' && 'value' in prev[lineItemsKey][0]) {
        updated[lineItemsKey] = [{ ...prev[lineItemsKey][0], value: newItems }, ...prev[lineItemsKey].slice(1)];
      } else {
        updated[lineItemsKey] = newItems;
      }

      return updated;
    });
  }, []);

  const handleTaxItemsChange = useCallback((newItems: NonNullable<DocumentFieldsResponse['tax_items']>) => {
    setEditedData((prev) => {
      const updated = { ...prev };

      if (Array.isArray(prev.tax_items) && prev.tax_items.length > 0 && typeof prev.tax_items[0] === 'object' && 'value' in prev.tax_items[0]) {
        updated.tax_items = [{ ...prev.tax_items[0], value: newItems }, ...prev.tax_items.slice(1)];
      } else {
        updated.tax_items = newItems;
      }

      return updated;
    });
  }, []);

  const hasChanges = JSON.stringify(editedData) !== JSON.stringify(document.extraction_data || {});

    return (
      <div className="space-y-4">

        <div className="space-y-3">
          {fields.filter(f => !(excludeLineItems && f.type === 'array')).map((field) => {
            const fieldConfidence = perFieldConf[field.key];
            const isLowConfidence = fieldConfidence !== undefined && fieldConfidence < confidenceThreshold;

            return (
            <div
              key={field.key}
              className={`border rounded-lg p-4 space-y-2 ${
                isLowConfidence
                  ? 'bg-yellow-50 dark:bg-yellow-900 border-yellow-300 dark:border-yellow-700'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  {field.label}
                </label>
                {fieldConfidence !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-semibold">
                      {isLowConfidence ? (
                        <span className="text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded">
                          Low Confidence: {(fieldConfidence * 100).toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900 px-2 py-1 rounded">
                          {(fieldConfidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {field.type === 'vendor' && (
                <ReferenceDropdown
                  referenceType="vendor"
                  value={field.value}
                  onChange={(id, label) => {
                    handleFieldChange(field.key, label);
                  }}
                  placeholder="Search and select vendor..."
                  erpConnectionId={erpConnectionId}
                />
              )}

              {field.type === "purchase_order" && (
                <ReferenceDropdown
                  referenceType="purchase_order"
                  value={field.value}
                  onChange={(id, label) => {
                    handleFieldChange(field.key, label);
                  }}
                  placeholder="Search and select purchase order..."
                />
              )}

              {field.type === 'number' && (
                <ExpressionNumberInput
                  step="0.01"
                  value={field.value ?? ''}
                  onValueChange={(value) => handleFieldChange(field.key, value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}

              {field.type === 'date' && (
                <input
                  type="date"
                  value={
                    field.value && typeof field.value === 'string'
                      ? field.value.split('T')[0]
                      : ''
                  }
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}

              {field.type === 'text' && (
                <input
                  type="text"
                  value={String(field.value || '')}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}

              {field.type === 'array' && (
                <LineItemsEditor
                  items={Array.isArray(field.value) ? field.value : []}
                  onChange={handleLineItemsChange}
                  documentType={documentType}
                />
              )}

              {field.type === 'tax_items' && (
                <TaxItemsEditor
                  items={Array.isArray(field.value) ? field.value : []}
                  onChange={handleTaxItemsChange}
                  isLoading={isLoading}
                />
              )}

              {/* Show candidates info if available */}
              {field.candidates && field.candidates.length > 1 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                      {field.candidates.length} candidates available
                    </summary>
                    <div className="mt-2 space-y-1 pl-2 text-gray-600 dark:text-gray-400">
                      {field.candidates.map((candidate, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="font-mono">{String(candidate.value)}</span>
                          <span className="ml-2 text-gray-500">{(candidate.confidence * 100).toFixed(0)}%</span>
                          <span className="ml-2 italic">{candidate.provenance}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
            );
          })}
        </div>

      </div>
    );
  }
);

EditableExtractedFields.displayName = 'EditableExtractedFields';

export default EditableExtractedFields;
